import { useMemo } from 'react'
import { Clock3, Repeat } from 'lucide-react'

import {
  buildJobDayTimelineEntries,
  countOverlappingJobTimelineEntries,
  formatJobTimelineClock,
  getJobCategoryColor,
  isRecurringJob,
  jobStatusAccentGradients,
  jobStatusColors,
  resolveJobCategoryKey,
} from '@/lib/job-list-display'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { cn } from '@/lib/utils'
import type { Job } from '@/types'

type JobsDayTimelineProps = {
  jobs: Job[]
  selectedDate: Date
  onOpenJob: (job: Job) => void
}

const HOUR_MARKS = Array.from({ length: 25 }, (_, index) => index)

function minutesToPercent(minutes: number) {
  return (minutes / (24 * 60)) * 100
}

export function JobsDayTimeline({
  jobs,
  selectedDate,
  onOpenJob,
}: JobsDayTimelineProps) {
  const entries = useMemo(
    () => buildJobDayTimelineEntries(jobs, selectedDate),
    [jobs, selectedDate],
  )
  const overlapCount = useMemo(
    () => countOverlappingJobTimelineEntries(entries),
    [entries],
  )

  if (entries.length === 0) {
    return null
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/90 shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 bg-muted/20 px-4 py-3">
        <div className="flex items-center gap-2">
          <Clock3 className="h-4 w-4 text-primary" />
          <div>
            <p className="text-sm font-medium text-foreground">Day timeline</p>
            <p className="text-xs text-muted-foreground">0–24 hr scale · one row per job</p>
          </div>
        </div>
        {overlapCount > 0 ? (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800">
            {overlapCount} overlap{overlapCount === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground">No overlaps</span>
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[760px] p-4">
          <div className="mb-3 grid grid-cols-[minmax(140px,180px)_minmax(0,1fr)] gap-3">
            <div />
            <div className="relative h-8">
              {HOUR_MARKS.map((hour) => (
                <div
                  key={hour}
                  className="absolute top-0 -translate-x-1/2"
                  style={{ left: `${(hour / 24) * 100}%` }}
                >
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {hour}
                  </span>
                  <div className="mx-auto mt-1 h-2 w-px bg-border/80" />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            {entries.map((entry) => {
              const job = entry.job
              const statusKey = job.status || 'draft'
              const categoryColor = getJobCategoryColor(resolveJobCategoryKey(job))
              const leftPercent = minutesToPercent(entry.startMinutes)
              const widthPercent = Math.max(minutesToPercent(entry.endMinutes - entry.startMinutes), 1.5)
              const jobLabel = formatJobTitleWithId({
                jobId: job.id,
                title: job.title,
                jobRef: job.job_ref,
              })
              const timeLabel = `${formatJobTimelineClock(entry.startMinutes)} – ${formatJobTimelineClock(entry.endMinutes)}`

              return (
                <div
                  key={String(job.id)}
                  className="grid grid-cols-[minmax(140px,180px)_minmax(0,1fr)] items-center gap-3"
                >
                  <button
                    type="button"
                    onClick={() => onOpenJob(job)}
                    className="min-w-0 text-left"
                  >
                    <p className="line-clamp-1 flex items-center gap-1 text-xs font-semibold text-foreground hover:text-primary">
                      {isRecurringJob(job) ? (
                        <Repeat className="h-3 w-3 shrink-0 text-violet-600" aria-hidden="true" />
                      ) : null}
                      <span className="truncate">{jobLabel}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground">{timeLabel}</p>
                  </button>

                  <div className="relative h-10 rounded-lg border border-border/60 bg-muted/20">
                    {HOUR_MARKS.map((hour) => (
                      <div
                        key={`${job.id}-grid-${hour}`}
                        className="absolute inset-y-0 border-l border-border/40 first:border-l-0"
                        style={{ left: `${(hour / 24) * 100}%` }}
                      />
                    ))}

                    <button
                      type="button"
                      onClick={() => onOpenJob(job)}
                      title={`${jobLabel} (${timeLabel})`}
                      className={cn(
                        'absolute top-1 bottom-1 overflow-hidden rounded-md border px-2 text-left shadow-sm transition hover:brightness-95',
                        jobStatusColors[statusKey] ?? jobStatusColors.draft,
                      )}
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`,
                      }}
                    >
                      <div
                        className={cn(
                          'absolute inset-y-0 left-0 w-1 bg-gradient-to-b',
                          jobStatusAccentGradients[statusKey] ?? categoryColor.accent,
                        )}
                      />
                      <div className="relative pl-2">
                        <p className="line-clamp-1 text-[10px] font-semibold leading-tight">
                          {jobLabel}
                        </p>
                        <p className="line-clamp-1 text-[9px] opacity-80">{timeLabel}</p>
                      </div>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
